import {
  createContext,
  forwardRef,
  useContext,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Check } from 'lucide-react';
import { cn } from '../lib/utils.js';
import {
  PopoverContentImpl,
  PopoverRoot,
  PopoverTriggerImpl,
  Slot,
  composeEventHandlers,
  useComposedRefs,
  usePopoverContext,
  type ContentProps,
  type TriggerProps,
} from './popover-internals.js';

/**
 * A menu of actions hanging off a button: the account menu in the header, the ⋯ on a row.
 *
 * Built on the in-house popover (top layer, light dismiss), with the keyboard model of the WAI-ARIA
 * menu button: arrows, Home/End, Enter/Space, Escape back to the trigger, and typing a letter to
 * jump to the item that starts with it. Disabled items are passed over by all of it. The exports
 * and props are the ones it had when it wrapped Radix, so no screen had to change.
 */

type EntryFocus = 'content' | 'first' | 'last';

interface MenuContextValue {
  /** Where focus lands when the menu opens: the first item from the keyboard, the menu itself from a pointer. */
  entryFocusRef: MutableRefObject<EntryFocus>;
  triggerId: string;
}

const MenuContext = createContext<MenuContextValue | null>(null);

function useMenuContext(consumer: string): MenuContextValue {
  const context = useContext(MenuContext);
  if (!context) throw new Error(`${consumer} must be rendered inside a DropdownMenu`);
  return context;
}

export interface DropdownMenuProps {
  children?: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Accepted for compatibility with the Radix API; the menu never blocks the rest of the page. */
  modal?: boolean;
  /** Accepted for compatibility with the Radix API; the menu has no submenus to mirror. */
  dir?: 'ltr' | 'rtl';
}

export function DropdownMenu({
  children,
  open,
  defaultOpen,
  onOpenChange,
  modal = true,
}: DropdownMenuProps): ReactElement {
  const entryFocusRef = useRef<EntryFocus>('content');
  const triggerId = useId();
  return (
    <PopoverRoot open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
      <MenuContext.Provider value={{ entryFocusRef, triggerId }}>{children}</MenuContext.Provider>
    </PopoverRoot>
  );
}

export const DropdownMenuTrigger = forwardRef<HTMLButtonElement, TriggerProps>(
  function DropdownMenuTrigger({ id, onClick, onKeyDown, onPointerDown, ...props }, ref) {
    const menu = useMenuContext('DropdownMenuTrigger');
    const context = usePopoverContext('DropdownMenuTrigger');
    // Whether the click about to arrive comes from Enter or Space rather than a pointer. Tracked
    // from the key press itself rather than guessed from the click's `detail`, which an `asChild`
    // element or assistive technology may not set the way a native button does.
    const fromKeyboardRef = useRef(false);
    return (
      <PopoverTriggerImpl
        id={id ?? menu.triggerId}
        aria-haspopup="menu"
        {...props}
        ref={ref}
        onPointerDown={composeEventHandlers(onPointerDown, () => {
          fromKeyboardRef.current = false;
        })}
        onClick={composeEventHandlers(onClick, () => {
          // From the keyboard, focus goes straight to the first item; from a pointer, to the
          // menu itself, so the first arrow press picks where to start.
          menu.entryFocusRef.current = fromKeyboardRef.current ? 'first' : 'content';
          fromKeyboardRef.current = false;
        })}
        onKeyDown={composeEventHandlers(onKeyDown, (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            fromKeyboardRef.current = true;
            return;
          }
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          menu.entryFocusRef.current = event.key === 'ArrowDown' ? 'first' : 'last';
          context.setOpen(true);
        })}
      />
    );
  },
);

export const DropdownMenuGroup = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { asChild?: boolean }
>(function DropdownMenuGroup({ asChild, ...props }, ref) {
  const Comp = (asChild ? Slot : 'div') as 'div';
  return <Comp role="group" {...props} ref={ref} />;
});

const ITEM_SELECTOR = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';

/** The items of this menu (not of one nested in it) that can be reached, in order. */
function enabledItems(menu: HTMLElement): HTMLElement[] {
  return Array.from(menu.querySelectorAll<HTMLElement>(ITEM_SELECTOR)).filter(
    (item) => !item.hasAttribute('data-disabled') && item.closest('[role="menu"]') === menu,
  );
}

function textOf(item: HTMLElement): string {
  return (item.dataset.textValue ?? item.textContent ?? '').trim().toLowerCase();
}

/**
 * The item typeahead should move to: the next one, after the current, whose text starts with
 * what has been typed. Typing the same letter again ("ccc") cycles through the items starting
 * with that letter rather than looking for one that starts with "ccc".
 */
function nextMatch(
  items: HTMLElement[],
  search: string,
  current: HTMLElement | null,
): HTMLElement | undefined {
  const repeated = search.length > 1 && Array.from(search).every((char) => char === search[0]);
  const wanted = (repeated ? search[0]! : search).toLowerCase();
  const start = current ? Math.max(items.indexOf(current), 0) : 0;
  let candidates = [...items.slice(start), ...items.slice(0, start)];
  if (wanted.length === 1) candidates = candidates.filter((item) => item !== current);
  const match = candidates.find((item) => textOf(item).startsWith(wanted));
  return match === current ? undefined : match;
}

export interface DropdownMenuContentProps extends ContentProps {
  /** Whether the arrow keys wrap from the last item to the first and back. Off, as it was. */
  loop?: boolean;
}

export const DropdownMenuContent = forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  function DropdownMenuContent(
    {
      className,
      sideOffset = 6,
      loop = false,
      onOpenAutoFocus,
      onCloseAutoFocus,
      onKeyDown,
      ...props
    },
    forwardedRef,
  ) {
    const menu = useMenuContext('DropdownMenuContent');
    const context = usePopoverContext('DropdownMenuContent');
    const contentRef = useRef<HTMLDivElement | null>(null);
    const ref = useComposedRefs(forwardedRef, contentRef);
    const searchRef = useRef('');
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const typeahead = (key: string, items: HTMLElement[], current: HTMLElement | null) => {
      searchRef.current += key;
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        searchRef.current = '';
      }, 1000);
      nextMatch(items, searchRef.current, current)?.focus({ preventScroll: true });
    };

    const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const menuElement = contentRef.current;
      if (!menuElement || event.ctrlKey || event.altKey || event.metaKey) return;
      const items = enabledItems(menuElement);
      const active = document.activeElement as HTMLElement | null;
      const current = active && items.includes(active) ? active : null;
      const index = current ? items.indexOf(current) : -1;
      const last = items.length - 1;
      const target = event.target as HTMLElement;
      const targetIsItem =
        target.matches(ITEM_SELECTOR) && target.closest('[role="menu"]') === menuElement;

      let next: HTMLElement | undefined;
      switch (event.key) {
        case 'ArrowDown':
          next = items[index === -1 ? 0 : index < last ? index + 1 : loop ? 0 : index];
          break;
        case 'ArrowUp':
          next = items[index === -1 ? last : index > 0 ? index - 1 : loop ? last : index];
          break;
        case 'Home':
        case 'PageUp':
          next = items[0];
          break;
        case 'End':
        case 'PageDown':
          next = items[last];
          break;
        case 'Tab':
          // A menu is one stop in the tab order: Tab leaves it, back to its button.
          event.preventDefault();
          context.setOpen(false);
          return;
        case 'Enter':
        case ' ':
          if (event.key === ' ' && searchRef.current !== '') {
            // Mid-word, a space is part of what is being typed ("Move up").
            event.preventDefault();
            typeahead(' ', items, current);
            return;
          }
          event.preventDefault();
          // The same path as a click, so `onSelect` behaves identically from either.
          if (targetIsItem) target.click();
          return;
        default:
          if (event.key.length === 1) typeahead(event.key, items, current);
          return;
      }
      event.preventDefault();
      next?.focus({ preventScroll: true });
    };

    return (
      <PopoverContentImpl
        ref={ref}
        role="menu"
        aria-orientation="vertical"
        aria-labelledby={context.triggerRef.current?.id || menu.triggerId}
        sideOffset={sideOffset}
        className={cn(
          'z-50 min-w-[12rem] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md outline-none',
          className,
        )}
        {...props}
        onOpenAutoFocus={composeEventHandlers(onOpenAutoFocus, (event) => {
          event.preventDefault();
          const menuElement = contentRef.current;
          if (!menuElement) return;
          // A word half-typed into the last opening is not the start of one in this.
          searchRef.current = '';
          // Read, not consumed: StrictMode runs this twice on mount, and both runs must agree.
          const entry = menu.entryFocusRef.current;
          const items = enabledItems(menuElement);
          const target =
            entry === 'first' ? items[0] : entry === 'last' ? items[items.length - 1] : undefined;
          (target ?? menuElement).focus({ preventScroll: true });
        })}
        onCloseAutoFocus={(event) => {
          // The next opening decides its own entry point; one opened from code gets the menu.
          menu.entryFocusRef.current = 'content';
          onCloseAutoFocus?.(event);
        }}
        onKeyDown={composeEventHandlers(onKeyDown, handleKeyDown)}
      />
    );
  },
);

interface MenuItemImplProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  asChild?: boolean;
  disabled?: boolean;
  /** What typeahead matches against, when the visible text is not it. */
  textValue?: string;
  /**
   * Called when the item is chosen, by click, Enter or Space. The menu then closes, unless the
   * handler calls `event.preventDefault()`.
   */
  onSelect?: (event: Event) => void;
}

const MenuItemImpl = forwardRef<HTMLDivElement, MenuItemImplProps>(function MenuItemImpl(
  {
    asChild,
    disabled = false,
    textValue,
    onSelect,
    onClick,
    onPointerMove,
    onPointerLeave,
    onFocus,
    onBlur,
    ...props
  },
  ref,
) {
  const context = usePopoverContext('DropdownMenuItem');
  const [highlighted, setHighlighted] = useState(false);
  const Comp = (asChild ? Slot : 'div') as 'div';

  const handleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    // An `asChild` link's own handler (a router `Link`) has already run and may have prevented
    // the browser's navigation; that is not a reason to skip the select. Only the item's own
    // `onClick` calling `preventDefault()` is.
    const preventedBefore = event.defaultPrevented;
    onClick?.(event);
    if (disabled) {
      event.preventDefault();
      return;
    }
    if (!preventedBefore && event.defaultPrevented) return;

    const item = event.currentTarget;
    const select = new CustomEvent('menu.itemSelect', { bubbles: true, cancelable: true });
    if (onSelect) item.addEventListener('menu.itemSelect', onSelect, { once: true });
    item.dispatchEvent(select);
    if (!select.defaultPrevented) context.setOpen(false);
  };

  return (
    <Comp
      role="menuitem"
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      data-disabled={disabled ? '' : undefined}
      data-highlighted={highlighted ? '' : undefined}
      data-text-value={textValue}
      {...props}
      ref={ref}
      onClick={handleClick}
      onPointerMove={composeEventHandlers(onPointerMove, (event) => {
        if (event.pointerType !== 'mouse') return;
        const item = event.currentTarget;
        // Hovering moves the keyboard's place too, so the arrows carry on from under the mouse.
        if (disabled) item.closest<HTMLElement>('[role="menu"]')?.focus({ preventScroll: true });
        else if (document.activeElement !== item) item.focus({ preventScroll: true });
      })}
      onPointerLeave={composeEventHandlers(onPointerLeave, (event) => {
        if (event.pointerType !== 'mouse') return;
        event.currentTarget.closest<HTMLElement>('[role="menu"]')?.focus({ preventScroll: true });
      })}
      onFocus={composeEventHandlers(onFocus, () => setHighlighted(true))}
      onBlur={composeEventHandlers(onBlur, () => setHighlighted(false))}
    />
  );
});

const itemClasses =
  'relative flex cursor-pointer select-none items-center gap-2 rounded-sm text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50';

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  MenuItemImplProps & { destructive?: boolean }
>(function DropdownMenuItem({ className, destructive, ...props }, ref) {
  return (
    <MenuItemImpl
      ref={ref}
      className={cn(
        itemClasses,
        'px-2 py-2.5 [&_svg]:size-4',
        destructive && 'text-destructive focus:bg-destructive/10 focus:text-destructive',
        className,
      )}
      {...props}
    />
  );
});

export const DropdownMenuLabel = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { asChild?: boolean }
>(function DropdownMenuLabel({ className, asChild, ...props }, ref) {
  const Comp = (asChild ? Slot : 'div') as 'div';
  return (
    <Comp ref={ref} className={cn('px-2 py-1.5 text-sm font-semibold', className)} {...props} />
  );
});

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { asChild?: boolean }
>(function DropdownMenuSeparator({ className, asChild, ...props }, ref) {
  const Comp = (asChild ? Slot : 'div') as 'div';
  return (
    <Comp
      ref={ref}
      role="separator"
      aria-orientation="horizontal"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
});

type CheckedState = boolean | 'indeterminate';

/**
 * An item that is ticked rather than acted on — a filter with several answers at once. Stays
 * open on select (the caller prevents the default) so several can be ticked in one visit.
 */
export const DropdownMenuCheckboxItem = forwardRef<
  HTMLDivElement,
  MenuItemImplProps & { checked?: CheckedState; onCheckedChange?: (checked: boolean) => void }
>(function DropdownMenuCheckboxItem(
  { className, children, checked = false, onCheckedChange, onSelect, ...props },
  ref,
) {
  const state = checked === 'indeterminate' ? 'indeterminate' : checked ? 'checked' : 'unchecked';
  return (
    <MenuItemImpl
      ref={ref}
      role="menuitemcheckbox"
      aria-checked={checked === 'indeterminate' ? 'mixed' : checked}
      data-state={state}
      className={cn(itemClasses, 'py-2.5 pl-8 pr-2', className)}
      {...props}
      onSelect={(event) => {
        onSelect?.(event);
        // Ticks even when `onSelect` kept the menu open; that is the usual reason to keep it open.
        onCheckedChange?.(checked === 'indeterminate' ? true : !checked);
      }}
    >
      <span className="absolute left-2 flex size-4 items-center justify-center">
        {checked !== false ? <Check aria-hidden="true" className="size-4" /> : null}
      </span>
      {children}
    </MenuItemImpl>
  );
});
